/**
 * BayView — kiosk-style "next on dock" page for FLDs (Phase F α).
 *
 * Route: /bay-view
 *
 * Two display modes, toggled with big buttons at the top:
 *   1. **By bay** — for each bay at the user's site, show the next 5 slots.
 *   2. **By time** — chronological list, next 5 inbound + next 5 outbound.
 *
 * Designed for big-text / FLD-friendly viewing on a dock screen.
 */
import { useEffect, useMemo, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { LayoutGrid, ArrowDownUp, RefreshCw } from "lucide-react";
import { listSitesLite, listBaysLite } from "@/lib/api";
import { useViewAsClient } from "@/lib/viewAsClient";
import { supabase } from "@/lib/supabaseClient";
import { statusBadgeClass } from "@/lib/constants";
import { formatInSiteTz } from "@/lib/datetime";
import SiteDocumentsLink from "@/components/SiteDocumentsLink";

const MODES = [
  { value: "by-bay",  label: "Bay list",  icon: LayoutGrid },
  { value: "by-time", label: "Time list", icon: ArrowDownUp },
];

const LIMIT = 5;

export default function BayView() {
  const { viewAsClientId } = useViewAsClient();
  const [sites, setSites]   = useState([]);
  const [siteId, setSiteId] = useState("");
  const [bays, setBays]     = useState([]);
  const [mode, setMode]     = useState("by-bay");
  const [bookings, setBookings] = useState([]);
  const [, setTick] = useState(0);

  // Auto-refresh every 60s
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 60_000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data } = await listSitesLite();
      if (cancelled) return;
      const filtered = (data || []).filter((s) => !viewAsClientId || s.client_id === viewAsClientId);
      setSites(filtered);
      const saved = localStorage.getItem("timeslot.bay_view_site");
      const initial = saved && filtered.find((s) => s.id === saved) ? saved : filtered[0]?.id || "";
      setSiteId(initial);
    })();
    return () => { cancelled = true; };
  }, [viewAsClientId]);

  useEffect(() => {
    if (!siteId) return undefined;
    localStorage.setItem("timeslot.bay_view_site", siteId);
    let cancelled = false;
    listBaysLite(siteId, { includeInactive: true }).then(({ data }) => {
      if (!cancelled) setBays(data || []);
    });
    return () => { cancelled = true; };
  }, [siteId]);

  // Load upcoming bookings (next 24h)
  useEffect(() => {
    if (!siteId) return undefined;
    let cancelled = false;
    (async () => {
      const start = new Date();
      const end   = new Date(start.getTime() + 24 * 3600 * 1000);
      const { data } = await supabase
        .from("lbs_bookings")
        .select(`
          id, reference_number, status, direction,
          scheduled_start, scheduled_end, bay_id,
          sender_company, receiver_company, vehicle_type,
          external_ref, internal_ref
        `)
        .eq("site_id", siteId)
        .gte("scheduled_start", start.toISOString())
        .lt("scheduled_start",  end.toISOString())
        .in("status", ["approved", "checked_in", "loading", "completed"])
        .order("scheduled_start", { ascending: true });
      if (!cancelled) setBookings(data || []);
    })();
    return () => { cancelled = true; };
  }, [siteId]);

  const selectedSite = useMemo(() => sites.find((s) => s.id === siteId), [sites, siteId]);
  const tz = selectedSite?.timezone;

  // Group by bay (next LIMIT per bay)
  const byBay = useMemo(() => {
    const m = new Map();
    for (const b of bays) m.set(b.id, []);
    for (const bk of bookings) {
      if (!m.has(bk.bay_id)) m.set(bk.bay_id, []);
      if (m.get(bk.bay_id).length < LIMIT) m.get(bk.bay_id).push(bk);
    }
    return m;
  }, [bookings, bays]);

  // For by-time: next 5 inbound + next 5 outbound
  const nextInbound  = useMemo(() => bookings.filter((b) => b.direction === "inbound" ).slice(0, LIMIT), [bookings]);
  const nextOutbound = useMemo(() => bookings.filter((b) => b.direction === "outbound").slice(0, LIMIT), [bookings]);

  const renderRow = (bk) => (
    <li key={bk.id} className="flex items-start justify-between gap-3 py-3 border-b last:border-0">
      <div className="flex-1">
        <div className="text-3xl font-bold tracking-tight">
          {tz ? formatInSiteTz(bk.scheduled_start, tz, { hour: "2-digit", minute: "2-digit" }) : new Date(bk.scheduled_start).toLocaleTimeString("en-AU", { hourCycle: "h23" })}
        </div>
        <div className="font-mono text-xs text-slate-500">{bk.reference_number}</div>
        <div className="text-base mt-1">{bk.sender_company || "—"} → {bk.receiver_company || "—"}</div>
        {(bk.external_ref || bk.internal_ref) && (
          <div className="text-xs text-slate-500">
            {bk.external_ref && <>Ext: {bk.external_ref}</>}
            {bk.external_ref && bk.internal_ref && " · "}
            {bk.internal_ref && <>Int: {bk.internal_ref}</>}
          </div>
        )}
      </div>
      <span className={`text-xs px-2 py-0.5 rounded ${statusBadgeClass(bk.status)}`}>{bk.status}</span>
    </li>
  );

  return (
    <div className="space-y-6" data-testid="bay-view-page">
      {/* Big mode-toggle buttons */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">
            {selectedSite?.name || "Bay view"}
          </h1>
          <p className="text-sm text-slate-500">Next 5 bookings, kiosk-friendly.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Select value={siteId} onValueChange={setSiteId}>
            <SelectTrigger className="w-48" data-testid="bay-view-site">
              <SelectValue placeholder="Pick site" />
            </SelectTrigger>
            <SelectContent>
              {sites.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
            </SelectContent>
          </Select>
          {MODES.map(({ value, label, icon: Icon }) => (
            <Button
              key={value}
              size="lg"
              variant={mode === value ? "default" : "outline"}
              onClick={() => setMode(value)}
              data-testid={`bay-view-mode-${value}`}
              className="h-12 px-5"
            >
              <Icon className="mr-2 h-5 w-5" /> {label}
            </Button>
          ))}
          <Button variant="ghost" size="icon" onClick={() => setTick((t) => t + 1)} data-testid="bay-view-refresh">
            <RefreshCw className="h-5 w-5" />
          </Button>
          <SiteDocumentsLink siteId={siteId} siteName={selectedSite?.name} />
        </div>
      </div>

      {!siteId ? (
        <div className="text-sm text-slate-500">Pick a site to see bay status.</div>
      ) : mode === "by-bay" ? (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {bays.map((bay) => (
            <Card key={bay.id} data-testid={`bay-view-bay-${bay.id}`}>
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-2 text-lg">
                  <span className="h-4 w-4 rounded-full" style={{ background: bay.color }} />
                  {bay.name}
                  {!bay.is_active && (
                    <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-slate-200 text-slate-600" data-testid={`bv-bay-inactive-${bay.id}`}>
                      inactive
                    </span>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ul>
                  {(byBay.get(bay.id) || []).length === 0 ? (
                    <li className="text-sm text-slate-400 italic py-3">No bookings in the next 24h.</li>
                  ) : (byBay.get(bay.id) || []).map(renderRow)}
                </ul>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Card data-testid="bay-view-inbound">
            <CardHeader><CardTitle className="text-lg">Next 5 — Inbound</CardTitle></CardHeader>
            <CardContent>
              <ul>
                {nextInbound.length === 0
                  ? <li className="text-sm text-slate-400 italic py-3">None scheduled.</li>
                  : nextInbound.map(renderRow)}
              </ul>
            </CardContent>
          </Card>
          <Card data-testid="bay-view-outbound">
            <CardHeader><CardTitle className="text-lg">Next 5 — Outbound</CardTitle></CardHeader>
            <CardContent>
              <ul>
                {nextOutbound.length === 0
                  ? <li className="text-sm text-slate-400 italic py-3">None scheduled.</li>
                  : nextOutbound.map(renderRow)}
              </ul>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
